import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { Trilha } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { useTrilhas } from "../estado/trilhas.js";
import { Icone } from "./Icone.js";
import { IconeAjustes, IconeFechar, IconeSeguir } from "./icones.js";

/** "2026-12-31" → "31/12/2026". */
export function diaCurto(dia: string | null): string | null {
  if (!dia) return null;
  const [a, m, d] = dia.split("-");
  return `${d}/${m}/${a}`;
}

/** A meta da trilha em uma linha: "10 por dia · alvo 31/12/2026". */
export function metaDaTrilha(t: Trilha): string {
  const alvo = diaCurto(t.dataAlvo);
  return [`${t.metaDiaria} por dia`, alvo ? `alvo ${alvo}` : null].filter(Boolean).join(" · ");
}

function noCelular(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(max-width: 47.99rem)").matches;
}

/**
 * O seletor de trilha: "trilha: **Faculdade** ▾" na lateral e no cabeçalho do
 * celular, e o diálogo de troca (9f no desktop, folha que sobe de baixo no
 * celular — 13g). Trocar troca o escopo inteiro (RN-01): itens, respostas,
 * metas, caderno e ofensiva. Arquivadas não aparecem; o aviso diz quais.
 */
export function SeletorDeTrilha() {
  const { estado, aplicar } = useTrilhas();
  const [aberto, setAberto] = useState(false);
  const [escolha, setEscolha] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [trocando, setTrocando] = useState(false);
  const dialogo = useRef<HTMLDialogElement>(null);
  const navegar = useNavigate();

  const todas = estado.nome === "pronto" ? estado.trilhas : [];
  const ativaId = estado.nome === "pronto" ? estado.ativaId : null;
  const trilhas = todas.filter((t) => !t.arquivada);
  const arquivadas = todas.filter((t) => t.arquivada);
  const ativa = trilhas.find((t) => t.id === ativaId) ?? null;
  const escolhida = trilhas.find((t) => t.id === (escolha ?? ativaId)) ?? null;
  const mesma = escolhida?.id === ativaId;

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);

  function abrir() {
    setEscolha(ativaId);
    setErro(null);
    setAberto(true);
  }

  async function confirmar() {
    if (!escolhida) return;
    if (mesma) {
      setAberto(false);
      return;
    }
    setTrocando(true);
    setErro(null);
    try {
      aplicar(await pedir("trilha.ativar", { params: { id: escolhida.id } }));
      setAberto(false);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível trocar de trilha.");
    } finally {
      setTrocando(false);
    }
  }

  const rotulo = ativa ? ativa.nome : "sem trilha";
  const celular = aberto && noCelular();
  const textoDoBotao = !escolhida
    ? "Escolha uma trilha"
    : mesma
      ? celular
        ? `Continuar em ${escolhida.nome}`
        : `Você já está em ${escolhida.nome}`
      : `Trocar para ${escolhida.nome}`;

  return (
    <>
      <button
        type="button"
        className="seletor-trilha t-anotacao"
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-label={`Trilha ativa: ${rotulo}. Trocar de trilha`}
        onClick={abrir}
      >
        <span className="seletor-trilha__rotulo">trilha:</span>
        <span className="seletor-trilha__nome">{rotulo}</span>
      </button>

      <dialog
        ref={dialogo}
        className="folha-dialogo folha-dialogo--gaveta"
        aria-labelledby="troca-titulo"
        onClose={() => setAberto(false)}
        onClick={(e) => {
          if (e.target === dialogo.current) setAberto(false);
        }}
      >
        <div className="folha-dialogo__topo">
          <h2 className="t-cartao" id="troca-titulo">
            Trocar de trilha
          </h2>
          <button type="button" className="botao-icone folha-dialogo__fechar" aria-label="Fechar" onClick={() => setAberto(false)}>
            <Icone glifo={IconeFechar} papel="acao" />
          </button>
        </div>
        <p className="t-ajuda">Trocar troca tudo: itens, respostas, metas, caderno e ofensiva.</p>

        {erro && (
          <p className="campo__erro" role="alert">
            {erro}
          </p>
        )}

        {trilhas.length === 0 ? (
          <p className="t-corpo t-2">Nenhuma trilha ativa. Crie uma no Acervo.</p>
        ) : (
          <div className="troca__lista" role="radiogroup" aria-label="Trilhas">
            {trilhas.map((t) => {
              const marcada = t.id === (escolha ?? ativaId);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={marcada}
                  className="troca__opcao"
                  onClick={() => setEscolha(t.id)}
                >
                  <span className={marcada ? "radio-mao radio-mao--marcado" : "radio-mao"} aria-hidden="true" />
                  <span className="carimbo" aria-hidden="true">
                    {t.iniciais}
                  </span>
                  <span>
                    <span className="t-cartao">{t.nome}</span>
                    <br />
                    <span className="t-ajuda">{metaDaTrilha(t)}</span>
                  </span>
                  {t.id === ativaId && <span className="troca__em-uso t-anotacao">em uso</span>}
                </button>
              );
            })}
          </div>
        )}

        {arquivadas.length > 0 && (
          <p className="t-ajuda">
            {arquivadas.map((t) => t.nome).join(", ")} {arquivadas.length === 1 ? "está arquivada e não aparece" : "estão arquivadas e não aparecem"} aqui.
          </p>
        )}

        <div className="troca__acoes">
          <button
            type="button"
            className="botao"
            disabled={!escolhida || trocando || (mesma && !celular)}
            onClick={() => void confirmar()}
          >
            <Icone glifo={IconeSeguir} papel="acao" />
            {trocando ? "Trocando…" : textoDoBotao}
          </button>
          <button
            type="button"
            className="botao botao--secundario troca__gerenciar"
            onClick={() => {
              setAberto(false);
              navegar("/acervo?aba=trilhas");
            }}
          >
            <Icone glifo={IconeAjustes} papel="acao" />
            Gerenciar trilhas
          </button>
        </div>
      </dialog>
    </>
  );
}
